import { useEffect, useMemo, useState } from 'react'

import { useGetReceptionDevice } from '@reception-web/common/api/www'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { MdOutlineVideocam } from 'react-icons/md'
import { TbWorld } from 'react-icons/tb'
import { useNavigate } from 'react-router-dom'
import Flag from 'react-world-flags'

import { Button, Layout, Text } from '../../components'
import { ERROR_TYPE, Language, PATH, Requirement } from '../../constants'
import { useContactProvider, useDeviceIdProvider } from '../../hooks'
import { preloadAlexWidgetScriptAsset } from '../../utils/alexConnectWidget'
import {
  resolveAutomatedCommunicationSystem,
  usesAlexAutomatedCommunicationSystem,
} from '../../utils/automatedCommunicationSystem'

export function SelectLanguageFrame() {
  const navigate = useNavigate()
  const { language, requirement, setLanguage } = useContactProvider()
  const [enabledContact, setEnabledContact] = useState(false)
  const { deviceId, getRoute } = useDeviceIdProvider()
  const receptionDevice = useGetReceptionDevice(deviceId ?? '')
  const { i18n } = useTranslation()
  const ja = i18n.getFixedT('ja', 'translation', 'frames.SelectLanguageFrame')
  const en = i18n.getFixedT('en', 'translation', 'frames.SelectLanguageFrame')
  const isReceptionDeviceLoading =
    receptionDevice.data === undefined && receptionDevice.error === undefined
  const isStartDisabled = !enabledContact || isReceptionDeviceLoading

  const navigateToAlexMaintenance = (
    reason: string,
    extra: Record<string, unknown> = {}
  ) => {
    Sentry.captureException(new Error('Failed to launch Alex Connect Widget'), {
      tags: {
        alert: 'critical',
        feature: 'alex_connect_widget',
      },
      extra: {
        deviceId,
        reason,
        ...extra,
      },
      fingerprint: ['alex-connect-widget', reason],
    })
    navigate(getRoute(PATH.maintenance), {
      state: { error: ERROR_TYPE.FAILED_TO_LAUNCH_ALEX },
    })
  }

  const automatedCommunicationSystem = useMemo(() => {
    if (isReceptionDeviceLoading || requirement !== Requirement.Other) {
      return undefined
    }

    return resolveAutomatedCommunicationSystem({
      apiValue: receptionDevice.data?.data?.automated_communication_system,
    })
  }, [
    isReceptionDeviceLoading,
    receptionDevice.data?.data?.automated_communication_system,
    requirement,
  ])

  useEffect(() => {
    if (
      automatedCommunicationSystem?.ok &&
      usesAlexAutomatedCommunicationSystem(automatedCommunicationSystem.value)
    ) {
      preloadAlexWidgetScriptAsset()
    }
  }, [automatedCommunicationSystem])

  const handleStartContact = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    if (isStartDisabled) {
      event.preventDefault()
      return
    }

    if (requirement !== Requirement.Other) {
      navigate(getRoute(PATH.dial))
      return
    }

    if (!automatedCommunicationSystem?.ok) {
      navigateToAlexMaintenance(
        automatedCommunicationSystem?.reason ??
          'reception_device_contract_missing'
      )
      return
    }

    if (
      !usesAlexAutomatedCommunicationSystem(automatedCommunicationSystem.value)
    ) {
      navigate(getRoute(PATH.dial))
      return
    }

    navigate(getRoute(PATH.alexConnect))
  }

  return (
    <Layout>
      <div className="mt-[40px] flex flex-col items-center gap-y-[40px] wide:mt-0 wide:gap-y-[15px]">
        <Text size="heading3" align="center">
          {ja('information')}
          <br />
          {en('information')}
        </Text>
        <div className="flex w-full items-center justify-center gap-x-[100px]">
          <Button
            className="h-[140px] w-[220px] wide:h-[135px]"
            size="l"
            square
            fill={enabledContact && language === Language.Japanese}
            onClick={() => {
              setLanguage(Language.Japanese)
              setEnabledContact(true)
            }}
          >
            <div className="flex flex-col items-center justify-center gap-3">
              <Text size="lg3" align="center">
                {ja('japanese')}
                <br />
                {en('japanese')}
              </Text>
              <Flag code="jp" width={50} className="border-1" />
            </div>
          </Button>
          <Button
            className="h-[140px] w-[220px] wide:h-[135px]"
            size="l"
            square
            fill={enabledContact && language === Language.English}
            onClick={() => {
              setLanguage(Language.English)
              setEnabledContact(true)
            }}
          >
            <div className="flex flex-col items-center justify-center gap-3">
              <Text size="lg3" align="center">
                {ja('english')}
                <br />
                {en('english')}
              </Text>
              <TbWorld size={50} />
            </div>
          </Button>
        </div>
        <Button
          className={`h-[130px] w-[350px] ${
            isStartDisabled ? 'opacity-40' : 'opacity-100'
          }`}
          size="l"
          square
          fill
          disabled={isStartDisabled}
          onClick={handleStartContact}
        >
          <div className="flex flex-col items-center justify-center gap-2">
            <Text size="lg3" align="center">
              {ja('contact')}
              <br />
              {en('contact')}
            </Text>
            <MdOutlineVideocam size={50} />
          </div>
        </Button>
        <div>
          <a
            href={getRoute(PATH.requirement)}
            onClick={(e) => {
              e.preventDefault()
              navigate(getRoute(PATH.requirement))
            }}
          >
            <Text className="underline" size="lg2" align="center">
              {ja('back')}
              <br />
              {en('back')}
            </Text>
          </a>
        </div>
      </div>
    </Layout>
  )
}
